// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Button, Spinner, TextArea, TextInput, useToast } from '@loa/ui-components';
import { proposalApi } from '../../../api/proposalApi';
import type { Proposal } from '../../../types/proposal';

export type WizardStepReviewSendProps = Readonly<{
  proposalId: string;
  proposal: Proposal;
  /** Returns the wizard to Step 3 (SoS builder). */
  onBack: () => void;
  /** Fires once the proposal has been sent to the client. */
  onSent: () => void;
}>;

function apiErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    return (err.response?.data as { message?: string } | undefined)?.message ?? fallback;
  }
  return fallback;
}

/**
 * Step 4 of the pension proposal wizard (Figma covers the client-facing
 * review-and-sign screen; this is the advisor-facing counterpart): an
 * embedded preview of the generated Statement of Suitability PDF, plus the
 * standard proposal send fields (client covering message, valid-until date).
 * **Send to client** echoes the proposal's existing title/details back
 * through the update endpoint (only `clientMessage`/`validUntil` change)
 * before calling the existing send endpoint.
 */
export function WizardStepReviewSend({
  proposalId,
  proposal,
  onBack,
  onSent,
}: WizardStepReviewSendProps) {
  const { showToast } = useToast();

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [pdfError, setPdfError] = useState<string | null>(null);

  const [clientMessage, setClientMessage] = useState(proposal.clientMessage ?? '');
  const [validUntil, setValidUntil] = useState(proposal.validUntil ?? '');
  const [sending, setSending] = useState(false);

  const pdfUrlRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setPdfLoading(true);
    setPdfError(null);
    proposalApi
      .getSuitabilityDocument(proposalId)
      .then((blob) => {
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        pdfUrlRef.current = url;
        setPdfUrl(url);
      })
      .catch((err) => {
        if (!cancelled) setPdfError(apiErrorMessage(err, 'Failed to load the Statement of Suitability document'));
      })
      .finally(() => {
        if (!cancelled) setPdfLoading(false);
      });
    return () => {
      cancelled = true;
      if (pdfUrlRef.current) {
        URL.revokeObjectURL(pdfUrlRef.current);
        pdfUrlRef.current = null;
      }
    };
  }, [proposalId]);

  const handleSend = async () => {
    if (sending) return;
    setSending(true);
    try {
      await proposalApi.updateProposal(proposalId, {
        title: proposal.title,
        clientMessage: clientMessage.trim() || undefined,
        validUntil: validUntil || undefined,
        details: proposal.details ?? {},
      });
      await proposalApi.sendProposal(proposalId);
      showToast('Proposal sent to client', 'success');
      onSent();
    } catch (err) {
      showToast(apiErrorMessage(err, 'Failed to send proposal to client'), 'error');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="pension-wizard-step" data-testid="wizard-step-review-send">
      <h2 className="pension-wizard-step__title">Review &amp; send</h2>

      <div className="review-send__preview" data-testid="review-send-preview">
        {pdfLoading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }} data-testid="review-send-pdf-loading">
            <Spinner />
          </div>
        )}
        {!pdfLoading && pdfError && (
          <div className="pension-wizard__error" data-testid="review-send-pdf-error">
            {pdfError}
          </div>
        )}
        {!pdfLoading && !pdfError && pdfUrl && (
          <iframe
            src={pdfUrl}
            title="Statement of Suitability preview"
            className="review-send__iframe"
            data-testid="review-send-iframe"
          />
        )}
      </div>

      <div className="review-send__fields">
        <TextArea
          label="Message to client"
          value={clientMessage}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setClientMessage(e.target.value)}
          rows={4}
          fluid
        />
        <TextInput
          label="Valid until (optional)"
          type="date"
          value={validUntil}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValidUntil(e.target.value)}
          fluid
        />
      </div>

      <div className="pension-wizard-step__footer">
        <Button variant="outlined" onClick={onBack} disabled={sending}>
          Back
        </Button>
        <Button
          variant="filled"
          onClick={handleSend}
          disabled={sending}
          loading={sending}
          data-testid="review-send-send-btn"
        >
          Send to client
        </Button>
      </div>
    </div>
  );
}

export default WizardStepReviewSend;
